/**
 * Booking List — central client-side filter engine.
 *
 * The list API returns each booking with its full product set (incl. the
 * productDetails JSON), payments and computed payment status. The dataset is
 * small, so the entire booking-wide filter (quick + advanced) runs client-side
 * over the fetched set. The powerful server `search` (ref / customer / traveler
 * / PNR / invoice) is kept separately and handled by the API.
 *
 * Logic (per spec):
 *   • within a group  → AND
 *   • between groups  → AND
 *   • across a booking's products → OR (at least one product matches)
 */

/* Spec product type ↔ stored productType */
export const PRODUCT_TYPE_OPTIONS = [
  { key: "Flight", label: "Flight" },
  { key: "Hotel", label: "Accommodation" },
  { key: "Transfer", label: "Transfer" },
  { key: "Package", label: "Package Holiday" },
  { key: "Insurance", label: "Insurance" },
  { key: "CarRental", label: "Car Rental" },
  { key: "Visa", label: "Visa" },
  { key: "Cruise", label: "Cruise" },
];

export const BOOKING_STATUS_OPTIONS = ["Pending", "Confirmed", "Cancelled"];
export const ORIGIN_OPTIONS = ["Online", "Offline", "API", "Fetched"];
export const PAYMENT_STATUS_OPTIONS = ["Unpaid", "Partial", "Paid", "Refunded", "Waiting Supplier Refund"];
export const PAYMENT_STATUS_LABEL = {
  Unpaid: "Unpaid",
  Partial: "Partially Paid",
  Paid: "Paid",
  Refunded: "Refunded",
  "Waiting Supplier Refund": "Waiting Supplier Refund",
};
export const PRODUCT_STATUS_OPTIONS = ["Pending", "Option", "Confirmed", "Cancelled"];
export const CUSTOMER_TYPE_OPTIONS = ["Private", "Company"];
export const TRAVELER_TYPE_OPTIONS = ["ADT", "CHD", "INF"];
export const SALE_TYPE_OPTIONS = [
  { key: "Package", label: "Margin Package" },
  { key: "Separate", label: "Separate Services" },
];

export const DEFAULT_FILTERS = {
  // Quick
  bookingStatus: [],
  productTypes: [],
  origins: [],
  paymentStatuses: [],
  serviceFrom: "",
  serviceTo: "",

  // 6.1 Booking
  bookingReference: "",
  invoiceNumber: "",
  saleType: "",
  createdFrom: "",
  createdTo: "",
  createdBy: "",
  locked: "", // "", "yes", "no"

  // 6.2 Customer & Traveler
  custFirstName: "",
  custLastName: "",
  custEmail: "",
  customerType: "",
  travFirstName: "",
  travLastName: "",
  travelerType: "",

  // 6.3 Products — presence toggles
  hasFlight: false,
  hasHotel: false,
  hasTransfer: false,
  hasPackage: false,
  hasInsurance: false,
  hasCarRental: false,
  hasVisa: false,
  hasCruise: false,
  productStatus: [],

  // 6.4 Product details (generic across productDetails JSON)
  flightAirline: "",
  flightNumber: "",
  flightPnr: "",
  flightDepAirport: "",
  flightArrAirport: "",
  hotelName: "",
  hotelCity: "",
  hotelCountry: "",
  transferPickup: "",
  transferDropoff: "",
  visaCountry: "",
  visaType: "",
  cruiseLine: "",
  cruiseCabin: "",

  // 6.5 Financial
  totalMin: "",
  totalMax: "",
  outstandingMin: "",
  outstandingMax: "",
  marginMin: "",
  marginMax: "",
  hasCredit: false,

  // 6.6 Payments & Refunds
  hasIncomingPayment: false,
  hasSupplierPayment: false,
  hasClientRefund: false,
  hasSupplierRefund: false,
  refundPending: false,

  // 6.7 Suppliers
  supplierName: "",
  supplierBookingRef: "",

  // 6.8 Documents & Status
  invoiceGenerated: "", // "", "yes", "no"
  documentsSent: "",
  bookingLocked: "",

  // 6.9 Advanced
  hasNotes: false,
};

/* ── helpers ── */
const lc = (v) => String(v ?? "").toLowerCase();
const has = (v) => v != null && String(v).trim() !== "";
const num = (v) => {
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

/** productDetails may be a JSON string or an object */
const pd = (p) => {
  let d = p?.productDetails;
  if (typeof d === "string") {
    try { d = JSON.parse(d); } catch { d = {}; }
  }
  return d || {};
};

/** Flatten any nested object/array into a single lowercase string for "contains" matching */
const deepText = (val) => {
  if (val == null) return "";
  if (typeof val !== "object") return lc(val);
  const out = [];
  const walk = (v) => {
    if (v == null) return;
    if (typeof v === "object") {
      (Array.isArray(v) ? v : Object.values(v)).forEach(walk);
    } else {
      out.push(lc(v));
    }
  };
  walk(val);
  return out.join(" ");
};

/** Earliest service date across all products of a booking (the "first service date"). */
export const bookingServiceDates = (raw) =>
  (raw?.products || [])
    .map((p) => {
      const d = pd(p);
      return p.serviceStartDate || d.checkIn || d.startDate || d.legs?.[0]?.departureDate || d.pickupDate || null;
    })
    .filter(Boolean)
    .sort();

const productMargin = (raw) => {
  const products = raw?.products || [];
  const profit = products.reduce((s, p) => s + (parseFloat(p.profit) || 0), 0);
  if (profit) return profit;
  const total = parseFloat(raw?.grandTotal || raw?.totalAmount || 0);
  const cost = products.reduce((s, p) => s + (parseFloat(p.costPrice) || 0), 0);
  return total - cost;
};

const paymentsOf = (raw) => raw?.payments || [];
const isRefund = (p) => lc(p.paymentType || p.type).includes("refund");
const isOutgoing = (p) => lc(p.direction || p.paymentType || p.type).includes("out") || lc(p.payeeType || p.party) === "supplier";

/** Count of meaningfully-set filters (for the "Filters • N" badge). */
export const activeFilterCount = (f) => {
  let n = 0;
  const arr = [
    "bookingStatus", "productTypes", "origins", "paymentStatuses", "productStatus",
  ];
  arr.forEach((k) => { if (Array.isArray(f[k]) && f[k].length) n++; });

  const text = [
    "bookingReference", "invoiceNumber", "saleType", "createdBy", "locked",
    "custFirstName", "custLastName", "custEmail", "customerType",
    "travFirstName", "travLastName", "travelerType",
    "flightAirline", "flightNumber", "flightPnr", "flightDepAirport", "flightArrAirport",
    "hotelName", "hotelCity", "hotelCountry", "transferPickup", "transferDropoff",
    "visaCountry", "visaType", "cruiseLine", "cruiseCabin",
    "supplierName", "supplierBookingRef", "invoiceGenerated", "documentsSent", "bookingLocked",
  ];
  text.forEach((k) => { if (has(f[k])) n++; });

  const ranges = [
    ["serviceFrom", "serviceTo"], ["createdFrom", "createdTo"],
    ["totalMin", "totalMax"], ["outstandingMin", "outstandingMax"], ["marginMin", "marginMax"],
  ];
  ranges.forEach(([a, b]) => { if (has(f[a]) || has(f[b])) n++; });

  const toggles = [
    "hasFlight", "hasHotel", "hasTransfer", "hasPackage", "hasInsurance", "hasCarRental", "hasVisa", "hasCruise",
    "hasCredit", "hasIncomingPayment", "hasSupplierPayment", "hasClientRefund", "hasSupplierRefund",
    "refundPending", "hasNotes",
  ];
  toggles.forEach((k) => { if (f[k]) n++; });

  return n;
};

/**
 * Map the filter state to the query params the booking-list API understands.
 * Only the genuinely reliable server filters are mapped (clean WHERE clauses):
 * single origin, single product type, creation-date range, invoiced.
 * Status comes from the tab and search from the search box (handled by the caller).
 */
export const serverParamsFromFilters = (f) => {
  const p = {};
  if (f.origins.length === 1) p.origin = f.origins[0];
  if (f.productTypes.length === 1) p.products = f.productTypes[0];
  if (has(f.createdFrom)) p.dateFrom = f.createdFrom;
  if (has(f.createdTo)) p.dateTo = f.createdTo;
  if (f.invoiceGenerated === "yes") p.invoiced = "true";
  else if (f.invoiceGenerated === "no") p.invoiced = "false";
  return p;
};

/**
 * Serialize the full filter state into API query params (server-side filtering).
 * Multi-selects become comma-joined; toggles become "true"; empty values dropped.
 * Note: booking status (tab/drawer) and the search box are merged in by the caller.
 */
export const toQueryParams = (f) => {
  const p = {};
  const txt = (k) => { if (has(f[k])) p[k] = String(f[k]).trim(); };

  if (f.origins.length) p.origin = f.origins.join(",");
  if (f.productTypes.length) p.products = f.productTypes.join(",");
  if (f.paymentStatuses.length) p.paymentStatus = f.paymentStatuses.join(",");
  if (f.productStatus.length) p.productStatus = f.productStatus.join(",");

  if (has(f.serviceFrom)) p.serviceFrom = f.serviceFrom;
  if (has(f.serviceTo)) p.serviceTo = f.serviceTo;
  if (has(f.createdFrom)) p.dateFrom = f.createdFrom;
  if (has(f.createdTo)) p.dateTo = f.createdTo;

  txt("bookingReference"); txt("invoiceNumber"); txt("createdBy");
  if (f.saleType) p.saleType = f.saleType;
  if (f.invoiceGenerated === "yes") p.invoiced = "true";
  else if (f.invoiceGenerated === "no") p.invoiced = "false";

  txt("custFirstName"); txt("custLastName"); txt("custEmail");
  if (f.customerType) p.customerType = f.customerType;
  txt("travFirstName"); txt("travLastName");
  if (f.travelerType) p.travelerType = f.travelerType;

  ["hasFlight", "hasHotel", "hasTransfer", "hasPackage", "hasInsurance", "hasCarRental", "hasVisa", "hasCruise"]
    .forEach((k) => { if (f[k]) p[k] = "true"; });

  ["flightAirline", "flightNumber", "flightPnr", "flightDepAirport", "flightArrAirport",
    "hotelName", "hotelCity", "hotelCountry", "transferPickup", "transferDropoff",
    "visaCountry", "visaType", "cruiseLine", "cruiseCabin",
    "supplierName", "supplierBookingRef"].forEach(txt);

  ["totalMin", "totalMax", "outstandingMin", "outstandingMax", "marginMin", "marginMax"]
    .forEach((k) => { if (has(f[k])) p[k] = f[k]; });
  if (f.hasCredit) p.hasCredit = "true";

  ["hasIncomingPayment", "hasSupplierPayment", "hasClientRefund", "hasSupplierRefund", "refundPending"]
    .forEach((k) => { if (f[k]) p[k] = "true"; });
  if (f.hasNotes) p.hasNotes = "true";

  return p;
};

/**
 * True when the filter set contains anything the API can't express on its own —
 * i.e. we must fetch a window and filter client-side. False → pure server pagination.
 */
export const requiresClientFiltering = (f) => {
  // Multi-selections the single-value API can't express (status is tab-driven)
  if (f.bookingStatus.length > 0) return true;
  if (f.origins.length > 1) return true;
  if (f.productTypes.length > 1) return true;
  if (f.paymentStatuses.length > 0) return true; // payment status: client-side only (API post-filter is page-local)
  if (f.productStatus.length > 0) return true;
  // Service date (the API's date range is creation date, not service date)
  if (has(f.serviceFrom) || has(f.serviceTo)) return true;

  const text = [
    "bookingReference", "invoiceNumber", "saleType", "createdBy", "locked",
    "custFirstName", "custLastName", "custEmail", "customerType",
    "travFirstName", "travLastName", "travelerType",
    "flightAirline", "flightNumber", "flightPnr", "flightDepAirport", "flightArrAirport",
    "hotelName", "hotelCity", "hotelCountry", "transferPickup", "transferDropoff",
    "visaCountry", "visaType", "cruiseLine", "cruiseCabin",
    "supplierName", "supplierBookingRef", "documentsSent", "bookingLocked",
  ];
  if (text.some((k) => has(f[k]))) return true;
  const ranges = ["totalMin", "totalMax", "outstandingMin", "outstandingMax", "marginMin", "marginMax"];
  if (ranges.some((k) => has(f[k]))) return true;
  const toggles = [
    "hasFlight", "hasHotel", "hasTransfer", "hasPackage", "hasInsurance", "hasCarRental", "hasVisa", "hasCruise",
    "hasCredit", "hasIncomingPayment", "hasSupplierPayment", "hasClientRefund", "hasSupplierRefund", "refundPending", "hasNotes",
  ];
  if (toggles.some((k) => f[k])) return true;
  return false;
};

/** Does a booking pass the full filter set? `b` is a mapped booking (with `.raw`). */
export const matchesFilters = (b, f) => {
  const raw = b.raw || {};
  const products = raw.products || [];
  const types = b.products || products.map((p) => p.productType);

  /* Quick — Booking status */
  if (f.bookingStatus.length && !f.bookingStatus.includes(b.bookingStatus)) return false;

  /* Quick — Product types (OR across the booking's products) */
  if (f.productTypes.length && !f.productTypes.some((t) => types.includes(t))) return false;

  /* Quick — Origin */
  if (f.origins.length && !f.origins.includes(b.origin)) return false;

  /* Quick — Payment status */
  if (f.paymentStatuses.length && !f.paymentStatuses.includes(b.paymentStatus)) return false;

  /* Quick — Service date range (first service date) */
  if (has(f.serviceFrom) || has(f.serviceTo)) {
    const dates = bookingServiceDates(raw);
    const first = dates[0];
    if (!first) return false;
    const d = first.slice(0, 10);
    if (has(f.serviceFrom) && d < f.serviceFrom) return false;
    if (has(f.serviceTo) && d > f.serviceTo) return false;
  }

  /* 6.1 Booking */
  if (has(f.bookingReference) && !lc(b.ref).includes(lc(f.bookingReference))) return false;
  if (has(f.invoiceNumber)) {
    const inv = lc(raw.invoiceNumber || raw.invoice_number || "");
    if (!inv.includes(lc(f.invoiceNumber))) return false;
  }
  if (has(f.saleType)) {
    const isPkg = !!(raw.is_package ?? raw.isPackage);
    if (f.saleType === "Package" && !isPkg) return false;
    if (f.saleType === "Separate" && isPkg) return false;
  }
  if (has(f.createdFrom) || has(f.createdTo)) {
    const c = (raw.bookingDate || raw.createdAt || "").slice(0, 10);
    if (!c) return false;
    if (has(f.createdFrom) && c < f.createdFrom) return false;
    if (has(f.createdTo) && c > f.createdTo) return false;
  }
  if (has(f.createdBy) && !lc(raw.createdBy).includes(lc(f.createdBy))) return false;
  if (has(f.locked)) {
    const locked = !!(raw.isLocked ?? raw.locked ?? raw.is_locked);
    if (f.locked === "yes" && !locked) return false;
    if (f.locked === "no" && locked) return false;
  }

  /* 6.2 Customer & Traveler */
  const fullName = lc(raw.customerName || b.customer?.name);
  if (has(f.custFirstName) && !fullName.includes(lc(f.custFirstName))) return false;
  if (has(f.custLastName) && !fullName.includes(lc(f.custLastName))) return false;
  if (has(f.custEmail) && !lc(raw.customerEmail || b.customer?.email).includes(lc(f.custEmail))) return false;
  if (has(f.customerType) && lc(raw.customerType || b.customer?.type) !== lc(f.customerType)) return false;
  // Traveler name/type — list payload has no traveler rows; routed through server search instead.

  /* 6.3 Product presence toggles */
  const hasType = (t) => types.includes(t);
  if (f.hasFlight && !hasType("Flight")) return false;
  if (f.hasHotel && !hasType("Hotel")) return false;
  if (f.hasTransfer && !hasType("Transfer")) return false;
  if (f.hasPackage && !hasType("Package")) return false;
  if (f.hasInsurance && !hasType("Insurance")) return false;
  if (f.hasCarRental && !hasType("CarRental")) return false;
  if (f.hasVisa && !hasType("Visa")) return false;
  if (f.hasCruise && !hasType("Cruise")) return false;

  /* Product status (OR across products) */
  if (f.productStatus.length &&
      !products.some((p) => f.productStatus.includes(p.productStatus))) return false;

  /* 6.4 Product details — match if ANY product contains the term in its details */
  const detailMatch = (typeKeys, term, ...paths) => {
    if (!has(term)) return true;
    const t = lc(term);
    return products.some((p) => {
      if (typeKeys && !typeKeys.includes(p.productType)) return false;
      const d = pd(p);
      // explicit paths first, then a deep fallback over the whole details blob
      const explicit = paths.map((path) => lc(path.split(".").reduce((o, k) => (o == null ? o : o[k]), d)));
      if (explicit.some((v) => v.includes(t))) return true;
      return deepText(d).includes(t) || lc(p.supplierName).includes(t);
    });
  };
  if (!detailMatch(["Flight"], f.flightAirline, "airline", "airlineName", "legs.0.airlineName")) return false;
  if (!detailMatch(["Flight"], f.flightNumber, "flightNumber", "legs.0.flightNumber")) return false;
  if (!detailMatch(["Flight"], f.flightPnr, "airlinePnr", "outboundPnr", "inboundPnr")) return false;
  if (!detailMatch(["Flight"], f.flightDepAirport, "legs.0.departureAirport", "departureAirport")) return false;
  if (!detailMatch(["Flight"], f.flightArrAirport, "arrivalAirport", "legs.0.arrivalAirport")) return false;
  if (!detailMatch(["Hotel", "Package"], f.hotelName, "hotelName", "destination")) return false;
  if (!detailMatch(["Hotel", "Package"], f.hotelCity, "city", "destinationCity")) return false;
  if (!detailMatch(["Hotel", "Package"], f.hotelCountry, "country", "destinationCountry")) return false;
  if (!detailMatch(["Transfer"], f.transferPickup, "schedule.pickupLocation", "pickupLocation")) return false;
  if (!detailMatch(["Transfer"], f.transferDropoff, "schedule.dropoffLocation", "dropoffLocation")) return false;
  if (!detailMatch(["Visa"], f.visaCountry, "destinationCountry", "country")) return false;
  if (!detailMatch(["Visa"], f.visaType, "visaType")) return false;
  if (!detailMatch(["Cruise"], f.cruiseLine, "cruiseLine", "shipName")) return false;
  if (!detailMatch(["Cruise"], f.cruiseCabin, "cabinType", "cabin")) return false;

  /* 6.5 Financial */
  const total = b.totalPrice ?? 0;
  const outstanding = b.saldo ?? 0;
  const margin = productMargin(raw);
  if (num(f.totalMin) != null && total < num(f.totalMin)) return false;
  if (num(f.totalMax) != null && total > num(f.totalMax)) return false;
  if (num(f.outstandingMin) != null && outstanding < num(f.outstandingMin)) return false;
  if (num(f.outstandingMax) != null && outstanding > num(f.outstandingMax)) return false;
  if (num(f.marginMin) != null && margin < num(f.marginMin)) return false;
  if (num(f.marginMax) != null && margin > num(f.marginMax)) return false;
  if (f.hasCredit && !(outstanding < 0)) return false;

  /* 6.6 Payments & Refunds */
  const pays = paymentsOf(raw);
  if (f.hasIncomingPayment && !((b.paidAmount ?? 0) > 0 || pays.some((p) => !isOutgoing(p) && !isRefund(p)))) return false;
  if (f.hasSupplierPayment && !pays.some((p) => isOutgoing(p) && !isRefund(p))) return false;
  if (f.hasClientRefund && !pays.some((p) => isRefund(p) && !isOutgoing(p))) return false;
  if (f.hasSupplierRefund && !pays.some((p) => isRefund(p) && isOutgoing(p))) return false;
  if (f.refundPending && !pays.some((p) => isRefund(p) && lc(p.status) === "pending")) return false;

  /* 6.7 Suppliers (OR across products) */
  if (has(f.supplierName) &&
      !products.some((p) => lc(p.supplierName).includes(lc(f.supplierName)))) return false;
  if (has(f.supplierBookingRef) &&
      !products.some((p) => lc(p.supplierReference).includes(lc(f.supplierBookingRef)))) return false;

  /* 6.8 Documents & Status */
  if (has(f.invoiceGenerated)) {
    const inv = !!raw.invoiced;
    if (f.invoiceGenerated === "yes" && !inv) return false;
    if (f.invoiceGenerated === "no" && inv) return false;
  }
  if (has(f.documentsSent)) {
    const sent = !!(raw.documentsSent ?? raw.documents_sent);
    if (f.documentsSent === "yes" && !sent) return false;
    if (f.documentsSent === "no" && sent) return false;
  }
  if (has(f.bookingLocked)) {
    const locked = !!(raw.isLocked ?? raw.locked ?? raw.is_locked);
    if (f.bookingLocked === "yes" && !locked) return false;
    if (f.bookingLocked === "no" && locked) return false;
  }

  /* 6.9 Advanced */
  if (f.hasNotes && !has(raw.internalNotes) && !has(raw.customerNotes)) return false;

  return true;
};

export const applyBookingFilters = (bookings, f) => bookings.filter((b) => matchesFilters(b, f));
